import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertTriangle, Copy as CopyIcon, FileText, Flame, Globe, Loader2,
  MessageSquare, Scale, Sparkles, Swords, Wand2,
} from "lucide-react";

const MODE_ICONS = {
  critical_reply: MessageSquare,
  roast_dunk: Flame,
  negative_review: FileText,
  devils_advocate: Scale,
};

const NEGATIVE_COST = 2;

export default function NegativeResponsePage() {
  const { user, refreshUser } = useAuth();
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("critical_reply");
  const [intensity, setIntensity] = useState("civil");
  const [applyClone, setApplyClone] = useState(true);
  const [inputType, setInputType] = useState("text"); // text | url
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [savageWarned, setSavageWarned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/negative/modes");
        setModes(data);
      } catch (e) {
        // fallback to hardcoded if endpoint fails
        setModes([
          { id: "critical_reply", label: "Critical Reply", blurb: "Sharp counter-argument" },
          { id: "roast_dunk", label: "Roast / Dunk", blurb: "Punchy dunk" },
          { id: "negative_review", label: "Negative Review", blurb: "Long-form criticism" },
          { id: "devils_advocate", label: "Devil's Advocate", blurb: "Strongest 'against' case" },
        ]);
      }
    })();
  }, []);

  const onIntensityChange = (next) => {
    if (next === "savage" && !savageWarned) {
      const ok = window.confirm(
        "Savage mode is sharper and snarkier. We still block personal attacks and slurs, but the tone is biting. Continue?",
      );
      if (!ok) return;
      setSavageWarned(true);
    }
    setIntensity(next);
  };

  const generate = async () => {
    const payload = {
      mode,
      intensity,
      apply_clone: applyClone,
    };
    if (inputType === "url") {
      if (!url.trim()) {
        toast.error("Paste the URL to react to.");
        return;
      }
      payload.target_url = url.trim();
    } else {
      if (text.trim().length < 10) {
        toast.error("Paste at least a sentence of the target.");
        return;
      }
      payload.target_text = text.trim();
    }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/negative/generate", payload);
      setResult(data);
      await refreshUser();
      toast.success("Response generated.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : formatError(detail);
      toast.error(msg || "Generation failed", { duration: 8000 });
    } finally {
      setGenerating(false);
    }
  };

  const copyContent = async () => {
    if (!result?.result?.content) return;
    await navigator.clipboard.writeText(result.result.content);
    toast.success("Copied to clipboard.");
  };

  const downloadTxt = () => {
    if (!result?.result?.content) return;
    const r = result.result;
    const out = [
      `=== ${r.title || "Negative Response"} ===`,
      `Mode: ${r.mode} · Intensity: ${r.intensity}`,
      "",
      r.content,
      "",
      "Key points:",
      ...(r.key_points || []).map((p) => `  • ${p}`),
      "",
      `Closing: ${r.closing_line || ""}`,
    ].join("\n");
    const blob = new Blob([out], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `negative-${r.mode}-${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const isMaster = user?.role === "admin" || user?.is_business_lifetime;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="negative-page">
        <header className="border-b-2 border-zinc-900 dark:border-zinc-100 pb-4">
          <div className="flex items-center gap-3">
            <Swords className="w-6 h-6" />
            <h1 className="text-2xl md:text-3xl font-black">Negative Response Generator</h1>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">
            Critical replies, roasts, negative reviews, and devil&apos;s-advocate takes — paste text or a URL, pick a mode, ship in 10 seconds.
          </p>
        </header>

        {/* Mode picker */}
        <section>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Mode</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {modes.map((m) => {
              const Icon = MODE_ICONS[m.id] || Sparkles;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`mode-${m.id}`}
                  className={`flex flex-col items-start gap-1 p-3 border text-left transition ${
                    active
                      ? "border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900"
                      : "border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-zinc-500"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <div className="font-bold text-sm">{m.label}</div>
                  <div className="text-[11px] opacity-75 leading-snug">{m.blurb}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Input */}
        <section className="border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-4 space-y-4">
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setInputType("text")}
              data-testid="input-type-text"
              className={`px-3 py-1.5 border ${inputType === "text" ? "border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold" : "border-zinc-300 dark:border-zinc-700"}`}
            >
              Paste text
            </button>
            <button
              type="button"
              onClick={() => setInputType("url")}
              data-testid="input-type-url"
              className={`px-3 py-1.5 border ${inputType === "url" ? "border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold" : "border-zinc-300 dark:border-zinc-700"}`}
            >
              <Globe className="inline w-3 h-3 mr-1" /> URL
            </button>
          </div>

          {inputType === "text" ? (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Target post / idea / claim</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Paste the post, claim, product description, or take you want to react against…"
                data-testid="negative-text-input"
                className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              />
            </label>
          ) : (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500">URL to react to</span>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/post-to-react-to"
                data-testid="negative-url-input"
                className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              />
              <span className="text-[11px] text-zinc-500 mt-1 block">We&apos;ll pull the title + description automatically.</span>
            </label>
          )}

          {/* Intensity */}
          <div>
            <span className="text-xs uppercase tracking-widest text-zinc-500">Tone</span>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                onClick={() => onIntensityChange("civil")}
                data-testid="intensity-civil"
                className={`flex-1 px-4 py-2 border text-sm font-bold ${intensity === "civil" ? "border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900" : "border-zinc-300 dark:border-zinc-700"}`}
              >
                Civil critique
              </button>
              <button
                type="button"
                onClick={() => onIntensityChange("savage")}
                data-testid="intensity-savage"
                className={`flex-1 px-4 py-2 border text-sm font-bold ${intensity === "savage" ? "border-red-600 bg-red-600 text-white" : "border-zinc-300 dark:border-zinc-700"}`}
              >
                <Flame className="inline w-3.5 h-3.5 mr-1" /> Savage
              </button>
            </div>
            {intensity === "savage" && (
              <div className="mt-2 text-[11px] text-red-600 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                Sharper tone. We still block personal attacks, slurs, and defamatory claims.
              </div>
            )}
          </div>

          {/* Clone toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={applyClone}
              onChange={(e) => setApplyClone(e.target.checked)}
              data-testid="apply-clone-toggle"
              className="w-4 h-4"
            />
            Match my Creator Clone voice (if trained)
          </label>

          <button
            type="button"
            onClick={generate}
            disabled={generating}
            data-testid="generate-negative-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold disabled:opacity-50"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate response ({isMaster ? "0" : NEGATIVE_COST} credits)</>}
          </button>
        </section>

        {/* Result */}
        {result?.result && (
          <section className="border-2 border-zinc-900 dark:border-zinc-100 bg-white dark:bg-zinc-950 p-5 space-y-3" data-testid="negative-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-black text-xl">{result.result.title}</h2>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {result.result.mode.replace("_", " ")} · {result.result.intensity} · {result.result.char_count} chars
                  {result.extracted_from_url && " · extracted from URL"}
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={copyContent} data-testid="copy-result-btn" className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-zinc-100 dark:hover:bg-zinc-900">
                  <CopyIcon className="w-3.5 h-3.5" /> Copy
                </button>
                <button type="button" onClick={downloadTxt} data-testid="download-result-btn" className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-zinc-100 dark:hover:bg-zinc-900">
                  .txt
                </button>
              </div>
            </div>

            {result.extracted_from_url && result.target_preview && (
              <div className="border-l-4 border-zinc-300 dark:border-zinc-700 pl-3 py-1 text-xs text-zinc-500">
                <span className="font-bold">Reacting to:</span> {result.target_preview}…
              </div>
            )}

            <div className="text-sm whitespace-pre-wrap leading-relaxed text-zinc-800 dark:text-zinc-200">
              {result.result.content}
            </div>

            {result.result.key_points?.length > 0 && (
              <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Key points</div>
                <ul className="text-sm space-y-1">
                  {result.result.key_points.map((p, i) => (
                    <li key={i} className="flex gap-2"><span className="text-zinc-400">•</span><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {result.result.closing_line && (
              <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3 text-sm italic text-zinc-700 dark:text-zinc-300">
                {result.result.closing_line}
              </div>
            )}

            <div className="text-xs text-zinc-500 text-right pt-2 border-t border-zinc-200 dark:border-zinc-800">
              Credits remaining: <span className="font-bold">{result.remaining_credits?.toLocaleString()}</span>
            </div>
          </section>
        )}

        {/* Hidden link to keep eslint happy */}
        <Link to="/roast" className="hidden">Roast page</Link>
      </div>
    </AppShell>
  );
}
